import { Injectable } from '@angular/core';
import { HttpService } from "../app/utility/http-service";
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../environments/environment';
import { catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';

export interface AttachmentHierarchyItem {
  id: string;
  clientId: string;
  parentHierarchyId?: string | null;
  folderName: string;
}

export interface Attachment {
  id: string;
  clientId?: string;
  hierarchyId?: string;
  filePath: string;
  fileName: string;
  description?: string;
  effectiveDate?: string;
  fileSizeBytes?: number;
}

export interface AttachmentHierarchy {
  id: string;
  clientId: string;
  parentHierarchyId?: string;
  folderName: string;
}

export interface AttachmentRecord {
  id: string;
  recordId?: string;
  attachmentId?: string;
  hierarchyId?: string;
  dataSetId?: string;
}

export interface AttachmentHierarchyNode {
  id: string;
  folderName: string;
  children: AttachmentHierarchyNode[];
  attachments: Attachment[];
}


@Injectable({
  providedIn: 'root'
})
export class AttachmentService {

  private readonly basePath = 'api/attachments';
  private get hierarchiesUrl(): string {
    return `${environment.apiConfig.uri}${this.basePath}/hierarchies`;
  }

  constructor(private http: HttpService, private httpClient: HttpClient) { }

  listHierarchies(clientId: string): Observable<AttachmentHierarchyItem[]> {
    const params = new HttpParams().set('clientId', clientId);
    return this.httpClient.get<AttachmentHierarchyItem[]>(this.hierarchiesUrl, { params })
      .pipe(catchError(err => throwError(() => err)));
  }

  createHierarchy(
    clientId: string,
    folderName: string,
    parentHierarchyId: string | null
  ): Observable<AttachmentHierarchyItem> {
    return this.httpClient.post<AttachmentHierarchyItem>(this.hierarchiesUrl, {
      clientId,
      folderName,
      parentHierarchyId: parentHierarchyId || null
    }).pipe(catchError(err => throwError(() => err)));
  }

  renameHierarchy(
    clientId: string,
    id: string,
    folderName: string
  ): Observable<void> {
    return this.httpClient.put<void>(`${this.hierarchiesUrl}/${encodeURIComponent(id)}`, {
      clientId,
      folderName
    }).pipe(catchError(err => throwError(() => err)));
  }

  deleteHierarchy(clientId: string, id: string): Observable<void> {
    const params = new HttpParams().set('clientId', clientId);
    return this.httpClient.delete<void>(`${this.hierarchiesUrl}/${encodeURIComponent(id)}`, { params })
      .pipe(catchError(err => throwError(() => err)));
  }

  /** Get attachments with optional filters */
  getAttachments(
    userName?: string,
    descriptionContains?: string,
    effectiveDate?: Date,
    hierarchyId?: string | null,
    clientId?: string, 
    onSuccess?: (data: Attachment[]) => void
  ): void {
    const queryParts: string[] = [];
    if (userName) queryParts.push(`userName=${encodeURIComponent(userName)}`);
    if (descriptionContains) queryParts.push(`descriptionContains=${encodeURIComponent(descriptionContains)}`);
    if (effectiveDate) queryParts.push(`effectiveDate=${encodeURIComponent(effectiveDate.toISOString())}`);
    // Only add hierarchyId if it is a valid GUID
    if (hierarchyId && hierarchyId !== 'null') {
      queryParts.push(`hierarchyId=${encodeURIComponent(hierarchyId)}`);
    }
    if (clientId) queryParts.push(`clientId=${encodeURIComponent(clientId)}`);

    const query = queryParts.length ? `?${queryParts.join('&')}` : '';
    const uri = `${this.basePath}${query}`;
    this.http.get<Attachment[]>(uri, onSuccess ?? (() => { }));
  }

  /** Download a specific attachment */
  downloadAttachment(
    id: string,
    fileName?: string,
    onComplete?: (success: boolean) => void
  ): void {
    const uri = `${this.basePath}/${id}`;
    this.http.getFile(uri, fileName ?? 'download', onComplete ?? (() => { }));
  }

  uploadAttachment(
    clientId: string,
    path: string,
    file: File,
    userName: string,
    description: string | null = null,  
    effectiveDate: Date | null = null,  
    hierarchyId?: string | null,
    recordId?: string | null,
    dataSetId?: string | null
  ): Observable<any> {

    const uri = `${this.basePath}/upload`;
    const formData = new FormData();

    formData.append('clientId', clientId);
    const pathParts = path.split('/').filter(p => p);
    pathParts.forEach(p => formData.append('path', p));

    formData.append('fileName', file.name);
    formData.append('file', file);
    formData.append('userName', userName);

    if (description) formData.append('description', description);
    if (effectiveDate) formData.append('effectiveDate', effectiveDate.toISOString());
    if (hierarchyId) formData.append('hierarchyId', hierarchyId);
    if (recordId) formData.append('recordId', recordId);
    if (dataSetId) formData.append('dataSetId', dataSetId);

    // Log the FormData contents to the console
    console.log('=== FORM DATA DEBUG START ===');
    formData.forEach((value, key) => {
      if (value instanceof File) {
        console.log(`${key}:`, {
          name: value.name,
          size: value.size,
          type: value.type,
          lastModified: value.lastModified
        });
      } else {
        console.log(`${key}:`, value);
      }
    });
    console.log('=== FORM DATA DEBUG END ===');
   
    return new Observable(observer => {
      this.http.post(
        uri,
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        undefined,
        formData
      );
    });
  }
  deleteAttachment(
    id: string,
    userName: string,
    onSuccess?: () => void,
    onError?: (err: any) => void
  ): void {
    const url = `${this.basePath}/${id}?userName=${encodeURIComponent(userName)}`;

    console.log('DELETE URL:', url);

    this.http.deleteReturn(
      url,
      onSuccess ?? (() => { }),
      onError
    );
  }
  /** Update attachment metadata (via POST) */
  updateAttachment(
    id: string,
    userName: string,
    description?: string,
    effectiveDate?: Date,
    hierarchyId?: string | null,
    onSuccess?: () => void
  ): void {
    const url = `${this.basePath}/Update`;
    const body: any = { id, userName };
    if (description) body.description = description;
    if (effectiveDate) body.effectiveDate = effectiveDate.toISOString();
    if (hierarchyId) body.hierarchyId = hierarchyId;

    console.log('Updating attachment:', body);
    this.http.post(url, onSuccess ?? (() => { }), undefined, body);
  }

  getAttachmentsWithHierarchy(
    clientId: string,
    recordId: string,
    hierarchyId?: string | null,
    userName?: string,
    dataSetId?: string | null,   
    descriptionContains?: string,
    effectiveDate?: Date,
    onSuccess?: (data: Attachment[]) => void
  ): void {

    if (!clientId?.trim()) {
      console.error('clientId is required');
      return;
    }

    if (!recordId?.trim()) {
      console.error('recordId is required');
      return;
    }

    const queryParts: string[] = [
      `clientId=${encodeURIComponent(clientId)}`,
      `recordId=${encodeURIComponent(recordId)}`
    ];
    if (hierarchyId?.trim() && hierarchyId !== 'null') {
      queryParts.push(`hierarchyId=${encodeURIComponent(hierarchyId)}`);
    }

    if (userName?.trim()) {
      queryParts.push(`userName=${encodeURIComponent(userName)}`);
    }
    if (dataSetId?.trim() && dataSetId !== 'null') {
      queryParts.push(`dataSetId=${encodeURIComponent(dataSetId)}`);
    }

    if (descriptionContains?.trim()) {
      queryParts.push(`descriptionContains=${encodeURIComponent(descriptionContains)}`);
    }

    if (effectiveDate) {
      queryParts.push(`effectiveDate=${encodeURIComponent(effectiveDate.toISOString())}`);
    }

    const uri = `${this.basePath}/with-hierarchy?${queryParts.join('&')}`;

    console.log('Fetching attachments with hierarchy:', uri);

    this.http.get<Attachment[]>(uri, (data: Attachment[]) => {
      onSuccess?.(data ?? []);
    }, true);
  }

  getHierarchyTree(
    clientId: string,
    recordId: string,             
    parentHierarchyId?: string | null,
    dataSetId?: string | null,    
    onSuccess?: (data: AttachmentHierarchyNode[]) => void
  ): void {
    if (!clientId?.trim()) return;
    if (!recordId?.trim()) return;

    const queryParts: string[] = [
      `clientId=${encodeURIComponent(clientId)}`,
      `recordId=${encodeURIComponent(recordId)}`
    ];

    if (parentHierarchyId && parentHierarchyId !== 'null') {
      queryParts.push(`parentHierarchyId=${encodeURIComponent(parentHierarchyId)}`);
    }

    if (dataSetId && dataSetId !== 'null') {   
      queryParts.push(`dataSetId=${encodeURIComponent(dataSetId)}`);
    }

    const query = `?${queryParts.join('&')}`;
    const uri = `${this.basePath}/hierarchy-tree${query}`;
    console.log('Fetching hierarchy tree:', uri);

    this.http.get<AttachmentHierarchyNode[]>(uri, onSuccess ?? (() => { }));
  }

  /** Get attachments for a specific record */
  getAttachmentsForRecord(
    clientId: string,
    recordId: string,
    dataSetId?: string,
    onSuccess?: (data: AttachmentRecord[]) => void
  ): void {
    if (!clientId?.trim()) {
      console.error('clientId is required');
      return;
    }

    if (!recordId?.trim()) {
      console.error('recordId is required');
      return;
    }
    const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    if (!guidRegex.test(recordId)) {
      console.error('recordId is not a valid GUID');
      return;
    }
    const params = new URLSearchParams({ clientId });
    if (dataSetId?.trim()) {
      params.append('dataSetId', dataSetId);
    }

    const uri = `${this.basePath}/for-record/${encodeURIComponent(recordId)}?${params.toString()}`;
    console.log('Fetching attachments for record:', uri);

    this.http.get<AttachmentRecord[]>(uri, (data: AttachmentRecord[]) => {
      onSuccess?.(data ?? []);
    });
  }

}
